import { Organizer, OrganizerType } from "../../../shared/types/OrganizerTypes"
import { getOrderPerson } from "../../../shared/utils/getOrderPerson"
import { OutlookMeetingValue, OutlookSessionType, SessionValue } from "./outlookCreateOrderTypes"

export const isOutlookMeetingValue = (v: SessionValue): v is OutlookMeetingValue =>
    v.type === OutlookSessionType.OUTLOOK_MEETING

/**
 * The people a saved order already belongs to. Needed to tell a mailbox `ORGANIZER` from a person,
 * because the id alone no longer does — see `isCalendarOrganizer`.
 */
export type OrderPeople = Readonly<{
    customerId?: number
    customerEmail?: string
    organizers?: Organizer[]
}>

/**
 * An `ORGANIZER` that is a *mailbox* rather than a person.
 *
 * On an order from a shared calendar, `ORGANIZER` is reserved for the calendar the meeting lives in
 * and the person travels as `CREATOR`. Not recognisable by "has no user id": the backend resolves the
 * address against its accounts, and a shared mailbox usually is one, so the entry comes back carrying
 * that account's id. The test is therefore whether that id is anyone on this order.
 *
 * Answers `false` without a `customerId`, which is what keeps the create path untouched, and for the
 * customer's own address, which the backend may resolve to another account of the same person.
 * Misfires only on an order holding four different people, a shape Manager does not build.
 */
export function isCalendarOrganizer(organizer: Organizer, people?: OrderPeople): boolean {
    if ((organizer.type ?? organizer.organizerType) !== OrganizerType.ORGANIZER) return false
    if (!organizer.email) return false

    // Orders created before the backend resolved the address still carry the id-less calendar entry
    // this used to look for. A free-text responsible is id-less too but never has an email.
    if (!organizer.id) return true

    const customerId = people?.customerId
    if (customerId === undefined) return false

    if (organizer.email.toLowerCase() === people?.customerEmail?.toLowerCase()) return false

    const { creator, responsible } = getOrderPerson(people?.organizers)

    return organizer.id !== customerId && organizer.id !== creator?.id && organizer.id !== responsible?.id
}

/**
 * Whether the meeting lives in a calendar shared with other people, which decides the organizer
 * layout: only a shared calendar needs an ORGANIZER of its own, taking the one id-less slot the API
 * tolerates away from the meeting responsible.
 *
 * `flag` is what the add-in reported. It is `undefined` when nobody reported anything — an add-in
 * older than the flag, or an order edited from Manager outside the Outlook flow — and only then is
 * the saved shape a usable fallback (see `isCalendarOrganizer`). Never derive this from the presence
 * of `ORGANIZER.email`: the add-in sends that address for every calendar.
 */
export function isSharedCalendarOrder(
    flag: boolean | undefined,
    calendarOrganizer?: Organizer,
    people?: OrderPeople,
): boolean {
    if (flag !== undefined) return flag

    return !!calendarOrganizer && isCalendarOrganizer(calendarOrganizer, people)
}

/**
 * The `ORGANIZER` carrying the Outlook calendar address, whether or not it also references a user.
 *
 * The address is what the API authorizes workgroup members by and only the add-in can read it, so
 * every save has to pass it on unchanged — dropping it on an edit would revoke the workgroup's
 * access. Deliberately wider than `isCalendarOrganizer`: on the user's own calendar the address
 * rides along with their own id, and it still must survive.
 */
export function findOrganizerAddress(organizers?: Organizer[]): Organizer | undefined {
    return organizers?.find(
        (organizer) => (organizer.type ?? organizer.organizerType) === OrganizerType.ORGANIZER && !!organizer.email,
    )
}

/**
 * The calendar entry as a request payload. The API echoes it back with both discriminators and with
 * an id — `0` when the address matches no account, that account's id when it does; neither belongs
 * in a write, and re-sending the id would bind the order to the mailbox's owner as a person. The
 * caller adds the discriminator its endpoint expects.
 */
export function toCalendarOrganizerPayload(organizer: Organizer): Pick<Organizer, "name" | "email"> {
    // Measured 2026-09-09: sending the id here makes the API overwrite RESPONSIBLE with the calendar
    // owner's user, so the order's host silently becomes the mailbox. Never re-add it.
    return { name: organizer.name, email: organizer.email }
}

/**
 * Who to seed the "meeting responsible" field with.
 *
 * `getOrderPerson` ranks ORGANIZER above CREATOR — a contract shared with the Outlook add-in and
 * deliberately left alone. That ranking assumes ORGANIZER is a person, which stops being true once
 * it carries the calendar: `person` then resolves to a mailbox address. Reachable both before an
 * Outlook order is saved (its only organizers come from the session) and after, on an order that
 * ended up without a RESPONSIBLE.
 */
export function getResponsibleSeed(
    organizers?: Organizer[],
    customerId?: number,
    customerEmail?: string,
): Organizer | undefined {
    const { person, creator } = getOrderPerson(organizers)

    return person && isCalendarOrganizer(person, { customerId, customerEmail, organizers }) ? creator : person
}
